import React, { useState, useEffect } from 'react';
import { useStore } from '../context/StoreContext.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { storage } from '../lib/firebase.ts';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { ChipDNA } from '../types.ts';
import { Save, Star, AlertCircle, MessageSquare, Heart, Info, X, CheckCircle, Upload, Trash2, Loader2, FileText } from 'lucide-react';
import { DEFAULT_DNA } from '../constants.ts';

type DNAObjectSection = 'core' | 'visual' | 'voice' | 'personality' | 'bio';

const ChipDNAProfile: React.FC = () => {
  const { dna, updateDNA } = useStore();
  const { user } = useAuth();
  const [localDna, setLocalDna] = useState<ChipDNA>(dna || DEFAULT_DNA);
  const [activeSection, setActiveSection] = useState<DNAObjectSection>('core');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isProcessing, setIsProcessing] = useState(false);
  
  useEffect(() => {
    if (dna) {
      setLocalDna(dna);
    }
  }, [dna]);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      // Create a deep copy to modify
      const finalDna = JSON.parse(JSON.stringify(localDna));

      // Check if visual.likenessImage is a base64 string (meaning new upload)
      if (finalDna.visual.likenessImage && finalDna.visual.likenessImage.startsWith('data:') && storage) {
        try {
           // Upload to Firebase Storage
           const storageRef = ref(storage, `settings/chip_dna/reference_${Date.now()}.jpg`);
           await uploadString(storageRef, finalDna.visual.likenessImage, 'data_url');
           const downloadURL = await getDownloadURL(storageRef);
           
           // Replace base64 with URL
           finalDna.visual.likenessImage = downloadURL;
           
           // Update local state to show URL as well (prevents re-uploading on next save)
           setLocalDna(prev => ({
             ...prev,
             visual: {
               ...prev.visual,
               likenessImage: downloadURL
             }
           }));
        } catch (uploadError) {
          console.error("Failed to upload reference image:", uploadError);
          alert("Failed to upload image to storage. Saving without image update.");
          // Fallback: keep previous or null if critical
        }
      }

      await updateDNA(finalDna);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error(error);
      alert("Failed to save Chip's DNA. Please try again.");
      setSaveStatus('idle');
    }
  };

  const updateField = (section: 'core' | 'visual' | 'voice' | 'personality', field: string, value: any) => {
    setLocalDna(prev => {
      // Ensure we are spreading an object to avoid "Spread types may only be created from object types"
      const sectionData = prev[section] ? { ...prev[section] } : {};
      return {
        ...prev,
        [section]: {
          ...sectionData,
          [field]: value
        }
      };
    });
  };

  const addArrayItem = (section: 'core' | 'visual' | 'voice' | 'personality', field: string, newItem: string) => {
    if (!newItem.trim()) return;
    const currentArray = (localDna[section] as any)[field] as string[] || [];
    updateField(section, field, [...currentArray, newItem]);
  };

  const removeArrayItem = (section: 'core' | 'visual' | 'voice' | 'personality', field: string, index: number) => {
    const currentArray = (localDna[section] as any)[field] as string[] || [];
    updateField(section, field, currentArray.filter((_, i) => i !== index));
  };

  // Helper to compress image client-side to fit into Firestore (max 1MB doc limit)
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          // Resize logic
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Compress to JPEG 0.7 quality to ensure small size
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    setIsProcessing(true);
    try {
      // Compress locally instead of uploading to Storage
      const base64String = await compressImage(file);
      
      setLocalDna(prev => ({
        ...prev,
        visual: {
          ...prev.visual,
          likenessImage: base64String
        }
      }));
    } catch (error) {
      console.error("Image processing failed", error);
      alert("Failed to process image. Please try another file.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveImage = () => {
    setLocalDna(prev => ({
      ...prev,
      visual: {
        ...prev.visual,
        likenessImage: null
      }
    }));
  };

  const ArrayInput: React.FC<{ 
    section: 'core' | 'visual' | 'voice' | 'personality', 
    field: string, 
    items: string[], 
    placeholder?: string 
  }> = ({ section, field, items = [], placeholder }) => {
    const [inputValue, setInputValue] = useState('');

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addArrayItem(section, field, inputValue);
        setInputValue('');
      }
    };

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2 mb-2">
          {items.map((item, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--bg-tertiary)] border border-[var(--border-default)] rounded-full text-sm text-[var(--text-primary)]">
              {item}
              <button onClick={() => removeArrayItem(section, field, idx)} className="text-[var(--text-tertiary)] hover:text-red-500">
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || "Type and press Enter..."}
            className="input-field"
          />
          <button 
            onClick={() => { addArrayItem(section, field, inputValue); setInputValue(''); }}
            className="btn-secondary py-2"
          >
            Add
          </button>
        </div>
      </div>
    );
  };

  const SidebarItem: React.FC<{ 
    id: DNAObjectSection, 
    icon: React.FC<{ size: number }>, 
    label: string 
  }> = ({ id, icon: Icon, label }) => (
    <button 
      onClick={() => setActiveSection(id)}
      className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
        activeSection === id 
          ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-sm ring-1 ring-[var(--border-subtle)]' 
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
      }`}
    >
      <Icon size={18} /> {label}
    </button>
  );

  if (!localDna || !localDna.core) {
      return <div className="p-8 text-center text-[var(--text-tertiary)]">Loading DNA Profile...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[var(--text-primary)]">
            Chip's DNA Profile
          </h1>
          <p className="text-[var(--text-secondary)] mt-1 max-w-2xl">
            This is the <strong className="text-[var(--text-primary)]">North Star</strong> for all AI generation. 
            Everything created in the Studio is downstream from the personality, voice, and rules defined here.
          </p>
        </div>
        <button 
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          className={`btn-primary flex items-center gap-2 transition-all ${saveStatus === 'saved' ? 'bg-green-600 border-green-600 text-white hover:bg-green-700 hover:border-green-700' : ''}`}
        >
          {saveStatus === 'saved' ? <CheckCircle size={18} /> : <Save size={18} />}
          {saveStatus === 'saving' ? 'Saving...' : (saveStatus === 'saved' ? 'Saved' : 'Save DNA')}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Navigation Sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0 space-y-2">
          <SidebarItem id="core" icon={Info} label="Core Identity" />
          <SidebarItem id="visual" icon={Star} label="Visual Identity" />
          <SidebarItem id="voice" icon={MessageSquare} label="Voice & Tone" />
          <SidebarItem id="personality" icon={Heart} label="Personality" />
          <SidebarItem id="bio" icon={FileText} label="Chip's Bio" />
          
          <div className="mt-8 p-4 bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-subtle)]">
            <h4 className="font-bold text-[var(--text-primary)] text-sm mb-2">Exhibit Happy</h4>
            <p className="text-xs text-[var(--text-secondary)] italic">
              "My story isn’t about flashy stunts. It’s about showing up for clients with creativity, clarity, and a whole lot of heart."
            </p>
            <div className="text-right mt-2 text-xs font-bold text-[var(--text-primary)]">- Chip</div>
          </div>
        </div>

        {/* Main Editor Area */}
        <div className="flex-1 space-y-6">
          
          {/* CORE SECTION */}
          {activeSection === 'core' && (
            <div className="card overflow-hidden">
              <div className="p-6 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <h2 className="text-xl font-heading font-bold text-[var(--text-primary)]">Core Identity</h2>
                <p className="text-sm text-[var(--text-secondary)]">Who Chip is at his heart.</p>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Name</label>
                    <input 
                      type="text" 
                      value={localDna.core?.name || ''}
                      onChange={(e) => updateField('core', 'name', e.target.value)}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Archetype</label>
                    <input 
                      type="text" 
                      value={localDna.core?.archetype || ''}
                      onChange={(e) => updateField('core', 'archetype', e.target.value)}
                      className="input-field"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Mission</label>
                  <textarea 
                    value={localDna.core?.mission || ''}
                    onChange={(e) => updateField('core', 'mission', e.target.value)}
                    className="input-field"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Values</label>
                  <ArrayInput section="core" field="values" items={localDna.core?.values || []} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Backstory</label>
                  <textarea 
                    value={localDna.core?.backstory || ''}
                    onChange={(e) => updateField('core', 'backstory', e.target.value)}
                    className="input-field"
                    rows={8}
                  />
                </div>
              </div>
            </div>
          )}

          {/* VISUAL SECTION */}
          {activeSection === 'visual' && (
            <div className="card overflow-hidden">
              <div className="p-6 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <h2 className="text-xl font-heading font-bold text-[var(--text-primary)]">Visual Identity</h2>
                <p className="text-sm text-[var(--text-secondary)]">Rules for generating images and videos.</p>
              </div>
              <div className="p-6 space-y-6">
                 {/* Physical Characteristics (Merged from Likeness) */}
                 <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Body Composition</label>
                    <textarea 
                      value={localDna.visual?.body || ''}
                      onChange={(e) => updateField('visual', 'body', e.target.value)}
                      className="input-field"
                      rows={3}
                      placeholder="Describe Chip's body shape, materials, and form..."
                    />
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Facial Features</label>
                    <textarea 
                      value={localDna.visual?.face || ''}
                      onChange={(e) => updateField('visual', 'face', e.target.value)}
                      className="input-field"
                      rows={2}
                      placeholder="Describe eyes, mouth, and expression..."
                    />
                 </div>

                 {/* Reference Image Upload */}
                 <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Reference Image</label>
                    <div className="rounded-xl border-2 border-dashed border-[var(--border-default)] p-6 transition-colors hover:border-[var(--accent-primary)] bg-[var(--bg-secondary)]">
                      {localDna.visual?.likenessImage ? (
                        <div className="relative group max-w-sm mx-auto">
                          <img 
                            src={localDna.visual.likenessImage} 
                            alt="Reference" 
                            className="w-full h-auto rounded-lg shadow-md" 
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                             <button 
                               onClick={handleRemoveImage}
                               className="bg-white/90 text-red-600 px-4 py-2 rounded-lg font-medium shadow-lg hover:bg-white flex items-center gap-2"
                             >
                               <Trash2 size={16} /> Remove Image
                             </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          {isProcessing ? (
                            <div className="flex flex-col items-center justify-center py-8">
                               <Loader2 className="animate-spin text-[var(--accent-primary)] mb-4" size={32} />
                               <p className="text-sm text-[var(--text-secondary)]">Processing Image...</p>
                            </div>
                          ) : (
                            <>
                              <div className="mx-auto w-12 h-12 rounded-full bg-[var(--bg-tertiary)] flex items-center justify-center mb-4">
                                <Upload className="text-[var(--text-tertiary)]" size={24} />
                              </div>
                              <p className="text-sm font-medium text-[var(--text-primary)]">Upload Reference Image</p>
                              <p className="text-xs text-[var(--text-secondary)] mt-1 mb-4">PNG or JPG</p>
                              <label className="btn-secondary py-2 px-4 cursor-pointer inline-block">
                                <span>Choose File</span>
                                <input 
                                  type="file" 
                                  accept="image/png, image/jpeg" 
                                  onChange={handleImageUpload}
                                  className="hidden" 
                                />
                              </label>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                 </div>

                 {/* Restrictions */}
                 <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/20">
                    <label className="block text-sm font-bold text-red-600 mb-2 flex items-center gap-2">
                      <AlertCircle size={16} /> Strict Restrictions
                    </label>
                    <ArrayInput section="visual" field="restrictions" items={localDna.visual?.restrictions || []} placeholder="e.g. No fingers..." />
                 </div>
              </div>
            </div>
          )}

          {/* VOICE SECTION */}
          {activeSection === 'voice' && (
            <div className="card overflow-hidden">
              <div className="p-6 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <h2 className="text-xl font-heading font-bold text-[var(--text-primary)]">Voice & Tone</h2>
                <p className="text-sm text-[var(--text-secondary)]">How Chip speaks in captions and scripts.</p>
              </div>
              <div className="p-6 space-y-6">
                <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Voice Style Guide</label>
                   <textarea 
                     value={localDna.voice?.styleGuide || ''}
                     onChange={(e) => updateField('voice', 'styleGuide', e.target.value)}
                     className="input-field"
                     rows={3}
                   />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div>
                     <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Tone Keywords</label>
                     <ArrayInput section="voice" field="tone" items={localDna.voice?.tone || []} />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Common Vocabulary</label>
                     <ArrayInput section="voice" field="keywords" items={localDna.voice?.keywords || []} />
                   </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/20">
                     <label className="block text-sm font-bold text-green-700 mb-2">DO</label>
                     <ArrayInput section="voice" field="dos" items={localDna.voice?.dos || []} />
                  </div>
                  <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/20">
                     <label className="block text-sm font-bold text-red-700 mb-2">DON'T</label>
                     <ArrayInput section="voice" field="donts" items={localDna.voice?.donts || []} />
                  </div>
                </div>

                <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Sample Phrases</label>
                   <ArrayInput section="voice" field="samplePhrases" items={localDna.voice?.samplePhrases || []} />
                </div>
              </div>
            </div>
          )}

          {/* PERSONALITY SECTION */}
          {activeSection === 'personality' && (
            <div className="card overflow-hidden">
               <div className="p-6 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <h2 className="text-xl font-heading font-bold text-[var(--text-primary)]">Personality & Facts</h2>
                <p className="text-sm text-[var(--text-secondary)]">The little details that make Chip real.</p>
              </div>
              <div className="p-6 space-y-6">
                 <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Personality Traits</label>
                   <ArrayInput section="personality" field="traits" items={localDna.personality?.traits || []} />
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Emotional Goals</label>
                   <ArrayInput section="personality" field="emotionalGoals" items={localDna.personality?.emotionalGoals || []} />
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Interests</label>
                   <ArrayInput section="personality" field="interests" items={localDna.personality?.interests || []} />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Favorites (Key: Value)</label>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                     {localDna.personality?.favorites && Object.entries(localDna.personality.favorites).map(([key, value], idx) => (
                       <div key={idx} className="flex items-center gap-2 bg-[var(--bg-tertiary)] p-2 rounded-lg border border-[var(--border-default)]">
                         <span className="font-semibold text-[var(--text-secondary)] text-sm min-w-[80px]">{key}:</span>
                         <input 
                           type="text"
                           value={value}
                           onChange={(e) => {
                             const newFavs = {...localDna.personality.favorites, [key]: e.target.value};
                             updateField('personality', 'favorites', newFavs);
                           }}
                           className="flex-1 bg-transparent text-sm text-[var(--text-primary)] focus:outline-none"
                         />
                       </div>
                     ))}
                   </div>
                 </div>
              </div>
            </div>
          )}

          {/* BIO SECTION */}
          {activeSection === 'bio' && (
            <div className="card overflow-hidden">
              <div className="p-6 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <h2 className="text-xl font-heading font-bold text-[var(--text-primary)]">Chip's Official Bio</h2>
                <p className="text-sm text-[var(--text-secondary)]">The definitive story and facts for public consumption.</p>
              </div>
              <div className="p-6">
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-3">Full Biography & Facts</label>
                <textarea 
                  value={localDna.core?.bio || ''}
                  onChange={(e) => updateField('core', 'bio', e.target.value)}
                  className="input-field min-h-[500px] font-sans leading-relaxed p-4"
                  placeholder="Paste the full bio here..."
                />
                <p className="text-xs text-[var(--text-tertiary)] mt-2">
                  Use this space for the long-form narrative. Markdown formatting is supported in some integrations.
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ChipDNAProfile;